import React, {
  useRef,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';

import CalendarEvent from './calendar-event';

import {
  ButtonContainer,
  Container,
  Message,
  SubmitButton,
} from './styles';

const Calendar = ({
  showSubmitButton,
  slotDuration,
  handleSave,
  handleSubmit,
  events,
  timeZone,
  toggleTime,
  loading,
  integrationCustomButton,
  sessionDuration,
}) => {
  const calendarRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [eventsValues, setEventsValues] = useState(events);

  const handleSaveCalendar = async () => {
    if (!loading) {
      const api = calendarRef.current.getApi();
      const evts = api.getEvents().map((evt) => ({
        startAt: moment(evt.startStr).utc().format(),
        endAt: moment(evt.endStr).utc().format(),
      }));
      await handleSave(evts);
    }
  };

  const handleSubmitCalendar = async () => {
    await handleSubmit();
  };

  const handleDateSelect = (selectInfo) => {
    if (!loading) {
      const calendarApi = selectInfo.view.calendar;
      let { endStr } = selectInfo;
      const { startStr, allDay } = selectInfo;

      if (moment(startStr) >= moment()) {
        if ((moment(endStr) - moment(startStr)) < sessionDuration * 60 * 1000) {
          endStr = moment(startStr).add(sessionDuration, 'minutes').format();
        }

        calendarApi.addEvent({
          title: '',
          start: startStr,
          end: endStr,
          allDay,
        }, true);
      }

      calendarApi.unselect();
    }
  };

  const handleEventAdd = () => {
    handleSaveCalendar();
  };

  const handleEventChange = () => {
    handleSaveCalendar();
  };

  const handleEventRemove = () => {
    handleSaveCalendar();
  };

  const renderEventContent = (info) => (
    <CalendarEvent
      event={info.event}
      timezone={timeZone}
      toggleTime={toggleTime}
    />
  );

  const slotLabelFormat = {
    hour: '2-digit',
    meridiem: 'short',
    hour12: !toggleTime,
    minute: '2-digit',
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragStop = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (!isDragging) {
      setEventsValues(events);
    }
  }, [events, isDragging]);

  return (
    <Container>
      <FullCalendar
        ref={calendarRef}
        plugins={[timeGridPlugin, interactionPlugin, momentTimezonePlugin]}
        initialView="timeGridWeek"
        headerToolbar={{
          left: 'prev,next today title',
          center: '',
          right: integrationCustomButton?.name,
        }}
        scrollTime="08:00:00"
        height={544}
        allDaySlot={false}
        timeZone={timeZone}
        weekends
        events={eventsValues}
        editable
        selectable
        slotDuration={slotDuration}
        select={handleDateSelect}
        eventContent={renderEventContent}
        eventAdd={handleEventAdd}
        eventChange={handleEventChange}
        eventRemove={handleEventRemove}
        eventDragStart={handleDragStart}
        eventDrop={handleDragStop}
        slotLabelFormat={slotLabelFormat}
        eventConstraint={{
          start: moment().format('YYYY-MM-DDTHH:mm'), // The start date and time where the user can drop a time event.
        }}
        dayCellClassNames={(info) => (info.isPast ? 'fc-day-disabled' : '')}
        customButtons={
          {
            [integrationCustomButton?.name]: {
              text: integrationCustomButton?.text,
              click: integrationCustomButton?.click,
            },
          }
        }
      />
      <ButtonContainer>
        {showSubmitButton && (
          <SubmitButton
            disabled={eventsValues.length === 0}
            onClick={handleSubmitCalendar}
          >
            Submit Availability
          </SubmitButton>
        )}
        {!showSubmitButton && (
          <Message>
            Your calendar is live. Any updates that you make to your availability will be
            autosaved and reflected in the link sent to future participants.
          </Message>
        )}
      </ButtonContainer>
    </Container>
  );
};

Calendar.propTypes = {
  showSubmitButton: PropTypes.bool,
  slotDuration: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  timeZone: PropTypes.string.isRequired,
  events: PropTypes.arrayOf(PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string,
  })),
  toggleTime: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  integrationCustomButton: PropTypes.shape({
    name: PropTypes.string,
    text: PropTypes.string,
    click: PropTypes.func,
  }),
  sessionDuration: PropTypes.number,
};

Calendar.defaultProps = {
  showSubmitButton: false,
  slotDuration: '00:30:00',
  events: [],
  loading: false,
  integrationCustomButton: null,
  sessionDuration: 30,
};

export default Calendar;
